import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Page from "components/ui/Page";
import Header from "components/ui/Header";
import { Box, Flex, Grid, Link, Spinner } from "theme-ui";
import { BackArrowIcon, CheckMarkIcon, EllipsisIcon, ProductsIcon, TrashIcon, PlusIcon } from "components/svg";
import { getPurchaseOrder, updatePurchaseOrder } from "api/PurchaseOrder";
import UserContext from "context/User";
import { getMyCompany } from "api/Companies";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Tab, Tabs } from "components/ui/Tabs";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import Separator from "components/ui/Separator";
import SelectInput from "components/forms/SelectInput";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import P from "components/typography/P";
import { Button } from "theme-ui";
import { getAllDepots, getDepot, getDepots } from "api/Providers/Depots";
import SearchField from "components/forms/SearchField";
import Checkbox from "components/forms/Checkbox";
import { useToast } from "components/toast";
import { trackActivity } from "api/ActivityTracker";

interface Product {
  id: string;
  name: string;
  addon: boolean;
  unit?: string;
  baseCost?: number;
  quantity?: number;
  entityId?: string;
  description?: string;
  markup?: number;
  cost?: number;
  price?: number;
  position?: number;
}

interface Depot {
  id: string;
  name: string;
  provider: { name: string };
  products: Product[];
}

interface POItem {
  id?: string;
  quantity: number;
  type?: string;
  entityId?: string;
  depotId: string;
  cost: number;
}

export interface UpdatePOData {
  projectName?: string;
  serviceDate?: string;
  status?: number;
  paymentStatus?: number;
  dueDate?: string;
  items?: POItem[][]; // Array of arrays
}

const PurchaseOrderEdit = () => {
  const [po, setPo] = useState<any>(null);
  const { user } = useContext<any>(UserContext);
  const { id } = useParams();
  const [company, setCompany] = useState<any>(null);
  const [tableItems, setTableItems] = useState<any[]>([]);
  const [originalTableItems, setOriginalTableItems] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedDepot, setSelectedDepot] = useState<Depot | null>(null);
  const [depotServiceOptions, setDepotServiceOptions] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedAddons, setSelectedAddons] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [canOpenEditModal, setCanOpenEditModal] = useState<boolean>(false);
  const [providerOptions, setProviderOptions] = useState<any[]>([]);
  const [depotOptions, setDepotOptions] = useState<any[]>([]);
  const [addOnOpen, setAddOnOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newAddons, setNewAddons] = useState<any[]>([]);

  const columnHelper = createColumnHelper();
  const toast = useToast();
  const navigate = useNavigate();
  const hasTrackedView = useRef(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | PO";
    _init();
  }, []);

  useEffect(() => {
    hasTrackedView.current = false;
    _init();
  }, [id]);

  const _init = async () => {
    if (user.role !== null) {
      const res = await getMyCompany();
      if (res.id) {
        setCompany(res);
      }
    } else {
      setCompany(user.company);
    }

    const poRes = await getPurchaseOrder({ data: { id: id } });
    if (poRes.id) {
      setPo(poRes);
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "purchase_orders",
          action: "view_purchase_order_edit",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "purchase_order",
          entityId: poRes.id,
          metadata: null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
      const allItems = poRes.lineitems?.flat() || [];

      const groupedByItemGroup: Record<number, any[]> = {};
      allItems.forEach((item: any) => {
        if (!groupedByItemGroup[item.itemGroup]) {
          groupedByItemGroup[item.itemGroup] = [];
        }
        groupedByItemGroup[item.itemGroup].push(item);
      });

      const structuredItems = Object.values(groupedByItemGroup).map((group: any[]) => {
        const service = group.find((item) => item.type === "service");
        const product = group.find((item) => item.type === "product");
        const addons = group.filter((item) => item.type === "addon-product");

        // Freeform items (e.g. "Change order adjustment") have no service/product structure
        if (!service && !product && group.length > 0) {
          const item = group[0];
          return {
            ...item,
            product: null,
            cost: item.cost ?? 0,
            price: item.price ?? 0,
            quantity: item.quantity ?? 1,
            serviceId: null,
            addons: [],
          };
        }

        return {
          ...service,
          product: product
            ? {
                id: product.id,
                name: product.name,
                description: product.description,
                unit: product.unit,
                entityId: product.entityId,
              }
            : null,
          cost: product?.cost ?? 0,
          price: product?.price ?? 0,
          quantity: product?.quantity ?? 0,
          serviceId: service?.entityId,
          addons: addons.map((addon) => ({
            id: addon.id,
            name: addon.name,
            description: addon.description,
            unit: addon.unit,
            baseCost: addon.cost,
            price: addon.price,
            quantity: addon.quantity,
            position: addon.position,
            entityId: addon.entityId,
          })),
        };
      });

      setTableItems(structuredItems);
      setOriginalTableItems(structuredItems);
    }
  };

  const toggleAddOnModal = (item?: any) => {
    setSearchTerm("");
    setAddOnOpen(!addOnOpen);
  };


  const submitUpdate = async (values: any) => {
    const previousPo = po;
    const mappedItems = tableItems.map((service) => {
      // Skip freeform/adjustment items — they're managed by the backend
      if (!service.product?.id) {
        return null;
      }
      const addonItems = (service.addons || [])
        .filter((addon: any) => addon.quantity > 0)
        .map((addon: any) => ({
          ...(addon.new ? {} : { id: addon.id }),
          quantity: addon.quantity,
          cost: Number(addon.baseCost),
          depotId: service.depot?.id,
          entityId: addon.entityId,
          type: "addon-product",
        }));

      const serviceItem = {
        id: service.id,
        quantity: 1,
        cost: 0,
        depotId: service.depot?.id,
        entityId: service.entityId,
        type: "service",
      };

      const productItem = {
        id: service.product.id,
        quantity: service.quantity,
        cost: Number(service.cost),
        depotId: service.depot?.id,
        entityId: service.product.entityId,
        type: "product",
      };

      return [serviceItem, productItem, ...addonItems];
    }).filter((x): x is any[] => x !== null);

    const servicesChanged =
      tableItems.length !== originalTableItems.length ||
      tableItems.some((current) => {
        const original = originalTableItems.find((s) => s.id === current.id);
        if (!original) return true;

        const fieldsMatch =
          original.quantity === current.quantity && Number(original.cost) === Number(current.cost) && original.depot?.id === current.depot?.id && original.serviceId === current.serviceId;

        const currentAddons = (current.addons || []).filter((a: any) => a.quantity > 0);
        const originalAddons = (original.addons || []).filter((a: any) => a.quantity > 0);

        const addonsMatch =
          currentAddons.length === originalAddons.length &&
          currentAddons.every((a: any) => {
            const match = originalAddons.find((o: any) => o.entityId === a.entityId);
            return match && a.quantity === match.quantity && Number(a.cost) === Number(match.cost);
          });

        return !(fieldsMatch && addonsMatch);
      });

    const updateData: UpdatePOData = {
      projectName: values.projectName,
      status: values.status,
      paymentStatus: values.paymentStatus,
      serviceDate: values.serviceDate,
      dueDate: values.dueDate,
      ...(servicesChanged ? { items: mappedItems } : {}),
    };

    if (values.dueDate) {
      updateData.dueDate = moment.unix(values.dueDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }

    if (values.serviceDate) {
      updateData.serviceDate = moment.unix(values.serviceDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }

    const res = await updatePurchaseOrder({ data: { id }, update: updateData });


if (res.id) {
  const changedFields: string[] = [];

  if (previousPo) {
    if (values.projectName !== previousPo.project?.projectName) changedFields.push("projectName");
    if (values.status !== previousPo.status) changedFields.push("status");
    if (values.paymentStatus !== previousPo.paymentStatus) changedFields.push("paymentStatus");

    const originalServiceDate = previousPo.serviceDate ? moment(previousPo.serviceDate).utc().format("YYYY-MM-DD") : null;
    const updatedServiceDate = values.serviceDate ? moment.unix(values.serviceDate).utc().format("YYYY-MM-DD") : null;
    if (originalServiceDate !== updatedServiceDate) changedFields.push("serviceDate");

    const originalDueDate = previousPo.dueDate ? moment(previousPo.dueDate).utc().format("YYYY-MM-DD") : null;
    const updatedDueDate = values.dueDate ? moment.unix(values.dueDate).utc().format("YYYY-MM-DD") : null;
    if (originalDueDate !== updatedDueDate) changedFields.push("dueDate");
  }

  if (servicesChanged) changedFields.push("items");

  if (changedFields.length > 0) {
    void trackActivity({
      module: "purchase_orders",
      action: "update_purchase_order",
      actionType: "update",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "purchase_order",
      entityId: res.id,
      metadata: { changedFields },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }

  setPo(res);
  toast.add("PO updated.", "var(--theme-ui-colors-success)");
  navigate(`/purchase-orders/${res.id}`);
}
  };

  const _validate = Yup.object().shape({
    projectName: Yup.string().required("Project name is required."),
  });

  const statusOptions = [
    { label: "New", value: 0 },
    { label: "Sent", value: 1 },
    { label: "Accepted", value: 2 },
    { label: "Billed", value: 3 },
    { label: "Denied", value: 4 },
    { label: "Closed", value: 5 },
  ];

  const paymentStatusOptions = [
    { label: "Open", value: 0 },
    { label: "Due", value: 1 },
    { label: "Paid", value: 2 },
    { label: "Partial Paid", value: 3 },
    { label: "Past Due", value: 4 },
    { label: "Cancelled", value: 5 },
  ];

  const toggleEditModal = (item?: any) => {
    if (item) {
      // Freeform items (e.g. "Change order adjustment") have no product — skip editing
      if (!item.product?.id) {
        return;
      }
      console.log(item);
      const bulkProductId = item.product.id;
      const addOnIds = item.addons.map((addon: any) => addon.addonId);
      const combinedProductIds = [bulkProductId, ...addOnIds].join(",");

      /**
       * A LINE WITH NO DEPOT STILL HAS TO BE EDITABLE.
       *
       * This read `item.depot.id` outright, so on a line whose depot was
       * never set the modal threw and simply never opened — which is why
       * it looked like there was no way to set a depot on a PO at all.
       * The four production POs in the dashboard's no-depot queue could
       * only be fixed with hand-written SQL because of this line.
       *
       * With no depot on the line, fall back to the ORDER's provider and
       * load that provider's depots, so the Depot picker below opens
       * populated and the fix is a two-click job.
       */
      const lineDepotId = item?.depot?.id ?? null;
      const lineProviderId =
        item?.depot?.provider?.id ?? po?.provider?.id ?? po?.providerId ?? null;

      if (user.role !== null && lineDepotId && lineProviderId) {
        getDepotDetails(lineDepotId, lineProviderId, combinedProductIds);
      } else if (lineDepotId) {
        setSelectedDepot(lineDepotId);
        setDepotServiceOptions([{ value: item.serviceId, label: item.name }]);
      } else {
        // No depot yet: offer every depot this provider has.
        setSelectedDepot(null);
        setDepotServiceOptions([{ value: item.serviceId, label: item.name }]);
        if (lineProviderId) {
          getDepots({ provider: { id: lineProviderId } }).then((res: any) => {
            setDepotOptions(
              (Array.isArray(res?.items) ? res.items : [])
                .filter((depot: any) => !depot?.deletedAt)
                .map((depot: any) => ({ value: depot.id, label: depot.name })),
            );
          });
        }
      }

      setSelectedRow(item);
      setSelectedAddons(item.addons);
      setSelectedService(item.serviceId);
      setSelectedProvider(lineProviderId);
      setCanOpenEditModal(true);
    } else {
      setSelectedRow(null);
      setSelectedDepot(null);
      setEditModalOpen(false);
      setCanOpenEditModal(false);
    }
  };

  const getDepotDetails = async (depotId: string, providerId: string, combinedProductIds?: any) => {
    const depotRes = await getDepot({ data: { id: depotId }, provider: { id: providerId } });

    if (depotRes.id) {
      setSelectedDepot(depotRes);

      const serviceOptions = depotRes.services.map((service: any) => ({
        value: service.id,
        label: service.name,
      }));

      setDepotServiceOptions(serviceOptions);
      const combinedServiceIds = (depotRes.services || []).map((service: any) => service.id);
      if (combinedProductIds) {
        getMatchingDepots(combinedProductIds, combinedServiceIds.join(","), providerId);
      }
    }
  };

  const getMatchingDepots = async (combinedIds: string, serviceIds: string, providerId: string) => {
    const depotRes = await getAllDepots({ query: { joins: ["products", "services"], filters: { $or: { "products.id": `${combinedIds}:in`, "services.id": `${serviceIds}:in` } } } });
    if (depotRes.items) {
      const uniqueProviders = Array.from(new Map(depotRes.items.map((item: any) => [item.provider.id, { label: item.provider.name, value: item.provider.id }])).values());

      setProviderOptions(uniqueProviders);

      const uniqueDepots = depotRes.items
        .filter((depot: any) => depot.provider.id === providerId)
        .map((depot: any) => ({
          value: depot.id,
          label: depot.name,
        }));
      setDepotOptions(uniqueDepots);
    }
  };

  const submitRemoveItem = (item: any) => {
    console.log("remove");
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "relatedService",
      cell: (info: any) => <Box sx={{ color: "#000", fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Related Service</span>,
    }),
    columnHelper.accessor((row: any) => row?.product?.name || 'N/A', {
      id: "productName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Bulk Product</span>,
    }),
    columnHelper.accessor((row: any) => row?.addons, {
      id: "addons",
      cell: (info: any) => {
        const addons = info.getValue();
        return Array.isArray(addons) && addons.filter((addon: any) => addon.quantity > 0).length > 0
          ? addons
              .filter((addon: any) => addon.quantity > 0)
              .map((addon: any) => addon.name)
              .join(", ")
          : "None";
      },
      header: () => <span>Add-on Products</span>,
    }),

    columnHelper.accessor((row: any) => row?.depot?.provider?.name || 'N/A', {
      id: "providerName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Provider</span>,
    }),

    columnHelper.accessor((row: any) => "actions", {
      id: "actions",
      cell: (info: any) => {
        const row = info.row.original;
        const isAdjustmentRow = !row?.product?.id;
        const items = [
          ...(isAdjustmentRow ? [] : [{ label: "Edit", onClick: () => toggleEditModal(info.row.original) }]),
          ...(tableItems.length > 1 && !isAdjustmentRow ? [{ label: "Remove", onClick: () => submitRemoveItem(info.row.original), style: { color: "var(--theme-ui-colors-danger)" } }] : []),
        ];

        if (items.length === 0) return null;

        return (
          <Box>
            <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  const submitItemEdit = async (values: any) => {
    const relatedServiceOption = depotServiceOptions.find((option) => option.value === values.relatedService);

    const relatedServiceLabel = relatedServiceOption?.label || "";

    let depotRes = null;

    if (user.role !== null) {
      depotRes = await getDepot({ data: { id: values.depot }, provider: { id: values.provider } });
    } else {
      depotRes = selectedRow.depot;
    }

    const services = [
      {
        id: selectedRow.id,
        entityId: values.relatedService,
        quantity: values.quantity,
        cost: values.unitCost,
        depot: depotRes,
        name: relatedServiceLabel,
        product: selectedRow.product,
        relatedService: values.relatedService,
        addons: values.addons,
        provider: values.provider,
        serviceId: values.relatedService,
      },
    ];

    setSelectedService(values.relatedService);

    setTableItems((prev) => prev.map((row) => (row.id === selectedRow.id ? services[0] : row)));
    toggleEditModal();
  };

  useEffect(() => {
    if (canOpenEditModal && selectedRow && selectedDepot) {
      setEditModalOpen(true);
      setCanOpenEditModal(false); // Reset flag after opening
    }
  }, [canOpenEditModal, selectedRow, selectedDepot]);

  const handleProviderChange = async (providerId: string, setFieldValue: any, fieldName: string) => {
    setFieldValue(fieldName, providerId); // Set provider FIRST

    const res = await getDepots({ provider: { id: providerId } });

    const depotOptions = (Array.isArray(res.items) ? res.items : [])
      .filter((depot: any) => !depot?.deletedAt)
      .map((depot: any) => ({
      value: depot.id,
      label: depot.name,
    }));

    setDepotOptions(depotOptions);

    if (depotOptions.length > 0) {
      const firstDepotId = depotOptions[0].value;
      setFieldValue("depot", firstDepotId);
      await getDepotDetails(firstDepotId, providerId);
    }
  };

  return (
    <>
      <Header></Header>
      {!po && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {po && (
        <Page
          title="Edit Purchase Order"
          subtitle="Edit the PO to update it's details."
          backAction={
            <Link variant="back" href={`/purchase-orders/${po.id}`}>
              <BackArrowIcon />
              {`Back to PO`}
            </Link>
          }
        >
          <Formik
            initialValues={{
              projectName: po.project?.projectName,
              serviceDate: po.serviceDate ? moment.utc(po.serviceDate).unix() : undefined,
              sequence: po.sequence,
              status: po.status,
              paymentStatus: po.paymentStatus,
              dueDate: po.dueDate ? moment.utc(po.dueDate).unix() : undefined,
            }}
            onSubmit={submitUpdate}
            validationSchema={_validate}
          >
            {({ isSubmitting, errors, values, submitCount, setFieldValue, handleSubmit }) => (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1, justifyContent: "flex-end" }}>
                      <Link href={`/purchase-orders/${po.id}`} sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save PO
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit basic information about the PO. Some fields are read-only for reference.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "20px" }}>
                          <TextInput name="poNumber" label="PO Number" value={`GMO-${String(po.sequence).padStart(5, "0")}`} readonly={true} $customStyles={{ flex: 1, marginBottom: "0px" }} />
                          <TextInput
                            name="projectName"
                            label="Project Name"
                            value={values.projectName}
                            onChange={(val: any) => setFieldValue("projectName", val)}
                            $errors={errors.projectName && submitCount > 0 ? errors.projectName : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                            readonly={true}
                          />
                        </Flex>
                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <SelectInput
                            name="status"
                            label="Status"
                            value={values.status}
                            options={statusOptions}
                            onChange={(val: any) => setFieldValue("status", val)}
                            $errors={errors.status && submitCount > 0 ? errors.status : null}
                            $customStyles={{ width: "100%" }}
                          />
                          <SelectInput
                            name="paymentStatus"
                            label="Payment Status"
                            value={values.paymentStatus}
                            options={paymentStatusOptions}
                            onChange={(val: any) => setFieldValue("paymentStatus", val)}
                            $errors={errors.paymentStatus && submitCount > 0 ? errors.paymentStatus : null}
                            $customStyles={{ width: "100%" }}
                          />
                        </Flex>
                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <SelectDate
                            name="serviceDate"
                            label="Estimated Service Date"
                            value={values.serviceDate}
                            onChange={(val: any) => setFieldValue("dueDate", val)}
                            $errors={errors.serviceDate && submitCount > 0 ? errors.serviceDate : null}
                            $customStyles={{ width: "50%", flexGrow: 1, marginBottom: "0px" }}
                            $removeMargin={true}
                          />
                          <SelectDate
                            name="dueDate"
                            label="Due Date"
                            value={values.dueDate}
                            onChange={(val: any) => setFieldValue("dueDate", val)}
                            $errors={errors.dueDate && submitCount > 0 ? errors.dueDate : null}
                            $customStyles={{ width: "50%", flexGrow: 1, marginBottom: "0px" }}
                            $removeMargin={true}
                          />
                        </Flex>
                      </Box>
                    </Grid>

                    <>
                      <Separator />
                      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                        <Box>
                          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                            Items
                          </H5>
                          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                            Edit the products and services associated with the Invoice.
                          </Subtext>
                        </Box>
                        <Box>
                          {tableItems && (
                            <TableBasic
                              data={tableItems}
                              columns={columns}
                              enablePaging={false}
                              enableSearch={false}
                              customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
                              disableSorting
                            />
                          )}
                        </Box>
                      </Grid>

                      <Separator />
                    </>
                  </Tab>
                </Tabs>
              </Form>
            )}
          </Formik>
          <Modal isOpen={editModalOpen} toggle={() => toggleEditModal()} centered size={user.role !== null ? "md" : "sm"}>
            <Formik
              initialValues={{
                relatedService: selectedService,
                addons: selectedAddons,
                provider: selectedProvider,
                depot: selectedDepot?.id,
                quantity: selectedRow?.quantity,
                unitCost:
                  Number(selectedRow?.cost) > 0
                    ? Number(selectedRow.cost)
                    : selectedRow?.price && selectedRow?.markup
                    ? Number((Number(selectedRow.price) / (1 + Number(selectedRow.markup) / 100)).toFixed(2))
                    : 0,
                product: selectedRow?.product,
              }}
              onSubmit={(values) => submitItemEdit(values)}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                errors,
                submitCount,
              }: {
                isSubmitting: boolean;
                submitCount: number;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                errors: any;
              }) => {
                console.log("selected service", selectedService);
                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (!product.addon) return false;
                    const existing = values.addons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                const submitRemoveAddonItem = (addonToRemove: any) => {
                  const updated = values.addons.map((addon: any) => (addon.entityId === addonToRemove.entityId ? { ...addon, quantity: 0 } : addon));
                  setFieldValue("addons", updated);
                  setSelectedAddons(updated);
                };

                useEffect(() => {
                  const mergedAddons = [...values.addons];
                  console.log("mergedAddons", mergedAddons);
                  console.log("selectedAddons", selectedAddons);

                  selectedAddons.forEach((newAddon) => {
                    const existingIndex = mergedAddons.findIndex((a) => a.entityId === newAddon.entityId);
                    console.log("existingIndex", mergedAddons[existingIndex]);

                    if (existingIndex > -1) {
                      mergedAddons[existingIndex] = {
                        ...mergedAddons[existingIndex],
                        quantity: newAddon.quantity ?? mergedAddons[existingIndex].quantity,
                        cost: newAddon.cost ?? mergedAddons[existingIndex].cost,
                      };
                    } else {
                      mergedAddons.push({ ...newAddon, quantity: newAddon.quantity || 1, new: true });
                    }
                  });
                  console.log("mergedAddons", mergedAddons);
                  setFieldValue("addons", mergedAddons);
                }, [selectedAddons]);

                const relatedServiceIdsInUse = tableItems.filter((row) => row.id !== selectedRow?.id).map((row) => row.serviceId);

                const filteredServiceOptions = depotServiceOptions.filter((option) => !relatedServiceIdsInUse.includes(option.value));

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleEditModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Edit PO item</ModalTitle>
                        <ModalSubtitle>{"Edit an item related to an Purchase Order."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <Flex sx={{ gap: "20px", flexDirection: user.role !== null ? "row" : "column" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="bulkProduct"
                            label="Bulk Product"
                            value={values.product.id}
                            options={[{ value: values.product.id, label: values.product.name }]}
                            $errors={errors.bulkProduct && submitCount > 0 ? errors.bulkProduct : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            disabled
                          />
                        </Box>
                        {user.role === null && (
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="relatedService"
                              label="Related Service"
                              value={values.relatedService}
                              options={filteredServiceOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              disabled={user.role === null}
                              onChange={(val: any) => setFieldValue("relatedService", val)}
                            />
                          </Box>
                        )}
                        {user.role === null && (
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="quantity"
                              label="Quantity"
                              type="number"
                              value={values.quantity}
                              onChange={(val: any) => setFieldValue("quantity", val)}
                              $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                              suffix={values.product.unit}
                            />
                          </Box>
                        )}
                        {user.role !== null && (
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="relatedService"
                              label="Related Service"
                              value={values.relatedService}
                              options={filteredServiceOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              disabled={user.role === null}
                              onChange={(val: any) => setFieldValue("relatedService", val)}
                            />
                          </Box>
                        )}
                      </Flex>
                      {user.role !== null && (
                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="provider"
                              label="Provider"
                              value={values.provider}
                              options={providerOptions}
                              $errors={errors.provider && submitCount > 0 ? errors.provider : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              onChange={(val: any) => handleProviderChange(val, setFieldValue, "provider")}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="depot"
                              label="Depot"
                              value={values.depot}
                              options={depotOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              onChange={(val: any) => setFieldValue("depot", val)}
                            />
                          </Box>
                        </Flex>
                      )}
                      {user.role !== null && (
                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="unitCost"
                              label="Unit Cost"
                              type="number"
                              value={values.unitCost}
                              onChange={(val: any) => setFieldValue("unitCost", val)}
                              prefix={"$"}
                              $errors={errors?.unitCost?.base_cost && submitCount > 0 ? errors.unitCost : null}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="quantity"
                              label="Quantity"
                              type="number"
                              value={values.quantity}
                              onChange={(val: any) => setFieldValue("quantity", val)}
                              $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                            />
                          </Box>
                        </Flex>
                      )}
                      <Flex>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Add-on Products
                        </Subtext>
                      </Flex>
                      <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", flexDirection: "column", marginBottom: "30px" }}>
                        <Flex sx={{ padding: "20px", gap: "20px", flexDirection: "column" }}>
                          {values.addons.filter((addon: any) => addon.quantity > 0).length > 0 &&
                            values.addons.map((addon: any, index: number) =>
                              addon.quantity > 0 ? (
                                <Flex
                                  key={index}
                                  sx={{
                                    gap: "10px",
                                    justifyContent: "flex-start",
                                    borderRadius: "5px",
                                    border: `1px solid ${themevals.colors.mdgrey}`,
                                    padding: "10px 15px",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "flex-start" }}>
                                    <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="500">
                                      {addon.name}
                                    </P>
                                  </Flex>
                                  <Flex sx={{ alignItems: "center", justifyContent: "flex-end", flex: user.role !== null ? 2 : 1, gap: "20px" }}>
                                    {user.role !== null && (
                                      <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                        <TextInput
                                          name={`addons[${index}].baseCost`}
                                          value={addon.baseCost}
                                          type="number"
                                          label="Base Cost"
                                          prefix={"$"}
                                          $customStyles={{ marginBottom: "0px" }}
                                          onChange={(val: any) => setFieldValue(`addons[${index}].baseCost`, val)}
                                          $errors={errors?.addons?.[index].baseCost && submitCount > 0 ? errors?.addons?.[index].baseCost : null}
                                        />
                                      </Flex>
                                    )}
                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].quantity`}
                                        value={addon.quantity}
                                        type="number"
                                        label={user.role !== null ? "Quantity" : ""}
                                        min={1}
                                        suffix={addon.unit}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].quantity`, val)}
                                        $errors={errors?.addons?.[index].quantity && submitCount > 0 ? errors?.addons?.[index].quantity : null}
                                        onKeyDown={(e: any) => {
                                          if (e.key === "0" && (e.currentTarget.value === "" || e.currentTarget.value === "0")) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onBlur={(e: any) => {
                                          const val = parseFloat(e.target.value);
                                          if (isNaN(val) || val < 1) {
                                            setFieldValue(`addons[${index}].quantity`, 1);
                                          }
                                        }}
                                      />
                                    </Flex>
                                  </Flex>
                                  {user.role !== null && (
                                    <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", paddingLeft: "10px", alignSelf: "stretch", width: "30px" }}>
                                      <Flex sx={{ alignSelf: "stretch", alignItems: "flex-end", paddingBottom: "10px" }}>
                                        <Box sx={{ cursor: "pointer" }} onClick={() => submitRemoveAddonItem(addon)}>
                                          <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                        </Box>
                                      </Flex>
                                    </Flex>
                                  )}
                                </Flex>
                              ) : null
                            )}
                          {values.addons.filter((addon: any) => addon.quantity > 0).length === 0 && (
                            <P fontFamily="Poppins" fontSize="14px" color={`${themevals.colors.dkgrey}`}>
                              There are no add-on products associated with this item.
                            </P>
                          )}
                        </Flex>
                        {availableAddons.length > 0 && (
                          <Flex sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", justifyContent: "flex-end" }}>
                            <BasicButton
                              type="button"
                              scheme="secondaryOutline"
                              $submitting={isSubmitting}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#000"
                              keepText={true}
                              onClick={() => toggleAddOnModal()}
                            >
                              {!isSubmitting && <PlusIcon fill={"#000"} />}
                              Add product
                            </BasicButton>
                          </Flex>
                        )}
                      </Flex>
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleEditModal()} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save item
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
          <Modal isOpen={addOnOpen} toggle={() => toggleAddOnModal()} centered size={"sm"}>
            <Formik
              initialValues={{ relatedService: selectedRow?.name, addons: selectedRow?.addons }}
              onSubmit={() => {
                setAddOnOpen(false);
              }}
              enableReinitialize
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                submitCount,
                errors,
              }: {
                isSubmitting: boolean;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                submitCount: number;
                errors: any;
              }) => {
                const availableAddons =
                  selectedDepot?.products?.filter((product) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                console.log("selectedAddons", selectedAddons);
                console.log("availableAddons", availableAddons);
                console.log("selectedDepot", selectedDepot?.products);

                const toggleSelectAll = () => {
                  if (newAddons.length === availableAddons.length) {
                    // If all are selected, deselect all
                    setNewAddons([]);
                  } else {
                    // Select all available add-ons
                    setNewAddons(
                      availableAddons.map((product) => ({
                        ...product,
                        addonId: product.id,
                        quantity: 1,
                        cost: product.cost,
                      }))
                    );
                  }
                };

                const handleAddSelectedAddons = () => {
                  setSelectedAddons((prevAddons) => [...prevAddons, ...newAddons]); // Merge selections into first modal
                  setNewAddons([]); // Clear temporary selections
                  setAddOnOpen(false); // Close the modal
                };

                const handleAddonSelect = (product: any) => {
                  const existing = selectedAddons.find((addon: any) => addon.entityId === product.entityId);
                  console.log("product", product);
                  setNewAddons((prev) =>
                    prev.some((addon) => addon.id === product.id)
                      ? prev.filter((addon) => addon.id !== product.id) // Remove if already selected
                      : [
                          ...prev,
                          {
                            ...product,
                            entityId: product.id,
                            quantity: 1,
                            baseCost: existing?.baseCost,
                            markup: existing?.markup,
                          },
                        ]
                  );
                };

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleAddOnModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Add products</ModalTitle>
                        <ModalSubtitle>{"Add available products at this depot."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                      <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center", paddingLeft: "11px" }}>
                        <Checkbox label="Select All" onChange={toggleSelectAll} checked={newAddons.length === availableAddons.length && availableAddons.length > 0} />
                      </Flex>
                      {availableAddons.map((product, idx) => (
                        <Flex
                          key={product.id}
                          sx={{
                            gap: "1rem",
                            alignItems: "center",
                            marginBottom: "10px",
                            border: `1px solid ${values.addons.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                            borderRadius: "6px",
                            height: "38px",
                            padding: "0px 10px",
                          }}
                        >
                          <Checkbox label={product.name} checked={newAddons.some((addon) => addon.id === product.id)} onChange={() => handleAddonSelect(product)} labelSize="14px" />
                        </Flex>
                      ))}
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => setAddOnOpen(false)} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="button" onClick={() => handleAddSelectedAddons()} $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <PlusIcon fill={"#fff"} />}
                        Add products
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
        </Page>
      )}
    </>
  );
};

export default PurchaseOrderEdit;
